/**
 * SyncManager - Centralized synchronization logic for multi-video comparison
 * 
 * Handles:
 * - Lap start synchronization using crossStart + lap timing
 * - Custom time offsets for manual alignment
 * - Time calculation for synchronized playback
 */

export class SyncManager {
  constructor() {
    this.sessions = new Map(); // videoId -> session data
    this.customOffsets = new Map(); // videoId -> time offset in seconds
    this.syncMode = 'lapStart'; // 'lapStart' | 'custom'
    this.selectedLaps = new Map(); // videoId -> lap number
    this.listeners = new Set();
    // No more masterVideoId - timeline is external
  }

  /**
   * Register a session for sync management
   */
  addSession(videoId, sessionData) {
    this.sessions.set(videoId, sessionData);
    this.customOffsets.set(videoId, 0);
    // Default to lap 1 for new sessions
    this.selectedLaps.set(videoId, 1);
  }

  /**
   * Remove a session from sync management
   */
  removeSession(videoId) {
    this.sessions.delete(videoId);
    this.customOffsets.delete(videoId);
    this.selectedLaps.delete(videoId);
  }

  /**
   * Set selected lap for a video
   */
  setSelectedLap(videoId, lapNumber) {
    this.selectedLaps.set(videoId, lapNumber);
  }

  /**
   * Get selected lap for a video
   */
  getSelectedLap(videoId) {
    return this.selectedLaps.get(videoId) || 1;
  }

  /**
   * Set sync mode
   */
  setSyncMode(mode) {
    this.syncMode = mode;
    
    // Reset custom offsets when switching to lap start mode
    if (mode === 'lapStart') {
      this.customOffsets.clear();
      this.sessions.forEach((_, videoId) => {
        this.customOffsets.set(videoId, 0);
      });
    }
    
    this.notifyListeners('syncModeChanged', { mode });
  }

  /**
   * Set custom offset for a video (switches to custom sync mode)
   */
  setCustomOffset(videoId, offsetSeconds) {
    this.customOffsets.set(videoId, offsetSeconds);
    
    // Automatically switch to custom mode when offset is applied
    if (this.syncMode !== 'custom') {
      this.syncMode = 'custom';
      this.notifyListeners('syncModeChanged', { mode: 'custom' });
    }
    
    this.notifyListeners('offsetChanged', { videoId, offset: offsetSeconds });
  }

  /**
   * Get the base start time for a video's selected lap
   */
  getLapStartTime(videoId) {
    const session = this.sessions.get(videoId);
    const selectedLap = this.getSelectedLap(videoId);
    
    if (!session || !session.lapData) return 0;
    
    const lap = session.lapData.find(l => l.number === selectedLap);
    return lap ? lap.startTime : 0;
  }

  /**
   * Calculate synchronized time for a video given the timeline time
   * @param {string} videoId - Video to calculate time for
   * @param {number} timelineTime - Current timeline time (independent of any video)
   * @param {string} referenceVideoId - Which video's lap timing to use as reference (usually first video)
   */
  calculateSyncedTime(videoId, timelineTime, referenceVideoId) {
    if (this.syncMode === 'lapStart') {
      return this.calculateLapStartSyncTime(videoId, timelineTime, referenceVideoId);
    } else {
      return this.calculateCustomSyncTime(videoId, timelineTime, referenceVideoId);
    }
  }

  /**
   * Calculate time for lap start synchronization
   * Timeline 10,000 = both videos at their selected lap start times
   * Timeline advances together from there
   */
  calculateLapStartSyncTime(videoId, timelineTime, referenceVideoId) {
    const videoStartTime = this.getLapStartTime(videoId);
    
    // Timeline 10,000 = lap start time, everything else is relative
    const TIMELINE_BASELINE = 10000;
    const relativeTime = timelineTime - TIMELINE_BASELINE;
    
    // Video time = lap start + relative time from baseline
    return videoStartTime + relativeTime;
  }

  /**
   * Calculate time for custom synchronization
   * Apply custom offsets on top of lap start sync
   */
  calculateCustomSyncTime(videoId, timelineTime, referenceVideoId) {
    const lapStartTime = this.calculateLapStartSyncTime(videoId, timelineTime, referenceVideoId);
    const customOffset = this.customOffsets.get(videoId) || 0;
    
    return lapStartTime + customOffset;
  }

  /**
   * Get current sync state for all videos
   * @param {number} timelineTime - Current timeline time
   * @param {string} referenceVideoId - Reference video for timing calculations
   */
  getSyncState(timelineTime, referenceVideoId) {
    const state = {
      syncMode: this.syncMode,
      timelineTime: timelineTime,
      videoTimes: new Map()
    };

    this.sessions.forEach((session, videoId) => {
      const syncedTime = this.calculateSyncedTime(videoId, timelineTime, referenceVideoId);
      state.videoTimes.set(videoId, {
        currentTime: syncedTime,
        selectedLap: this.getSelectedLap(videoId),
        customOffset: this.customOffsets.get(videoId) || 0
      });
    });

    return state;
  }

  /**
   * Handle lap selection change
   */
  handleLapSelection(videoId, lapNumber) {
    this.setSelectedLap(videoId, lapNumber);
    
    // Reset to lap start sync mode when laps change
    this.setSyncMode('lapStart');
    
    this.notifyListeners('lapSelectionChanged', { videoId, lapNumber });
  }

  /**
   * Handle individual video time change (nudge)
   * @param {string} videoId - Video that was nudged
   * @param {number} newTime - New video time after nudge
   * @param {number} timelineTime - Current timeline time
   * @param {string} referenceVideoId - Reference video for calculations
   */
  handleVideoTimeChange(videoId, newTime, timelineTime, referenceVideoId) {
    // Calculate what the synced time should be without custom offset
    const expectedTime = this.calculateLapStartSyncTime(videoId, timelineTime, referenceVideoId);
    
    // Calculate the offset needed to reach the desired time
    const offset = newTime - expectedTime;
    
    this.setCustomOffset(videoId, offset);
  }

  /**
   * Add event listener
   */
  addEventListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of an event
   */
  notifyListeners(eventType, data) {
    this.listeners.forEach(listener => {
      try {
        listener(eventType, data);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });
  }

  /**
   * Reset all sync state
   */
  reset() {
    this.customOffsets.clear();
    this.selectedLaps.clear();
    this.syncMode = 'lapStart';
    
    // Re-initialize offsets and default laps for existing sessions
    this.sessions.forEach((_, videoId) => {
      this.customOffsets.set(videoId, 0);
      this.selectedLaps.set(videoId, 1);
    });
    
    this.notifyListeners('reset', {});
  }
}

export default SyncManager; 